"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import AppLogo from "@/components/ui/AppLogo";
import { navigationService, NavigationItem } from "@/lib/services/navigationService";
import { createClient } from "@/lib/supabase/client";

export default function NavBar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navLinks, setNavLinks] = useState<NavigationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [businessHours, setBusinessHours] = useState('Mon–Fri: 9 am – 5 pm');
  const [businessLocation, setBusinessLocation] = useState('Toronto, Ontario, Canada');

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    loadNavigation();
    loadSiteSettings();
  }, []);

  const loadNavigation = async () => {
    try {
      setLoading(true);
      const items = await navigationService.getActive();
      setNavLinks(items);
    } catch (error: any) {
      console.error('Failed to load navigation:', error.message);
      setNavLinks([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSiteSettings = async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('site_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['business_hours', 'business_location']);
      if (error) throw error;
      if (data) {
        data.forEach((row: { setting_key: string; setting_value: string }) => {
          if (row.setting_key === 'business_hours' && row.setting_value) {
            setBusinessHours(row.setting_value);
          }
          if (row.setting_key === 'business_location' && row.setting_value) {
            setBusinessLocation(row.setting_value);
          }
        });
      }
    } catch (error: any) {
      console.error('Failed to load site settings:', error.message);
    }
  };

  const socialLinks = [
  {
    href: "https://www.facebook.com/gogedit/",
    label: "Facebook",
    icon:
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>

  },
  {
    href: "https://twitter.com/GoGeditInc",
    label: "X",
    icon:
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>

  },
  {
    href: "https://www.instagram.com/gogeditinc/",
    label: "Instagram",
    icon:
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
        </svg>

  },
  {
    href: "https://www.linkedin.com/company/gogedit",
    label: "LinkedIn",
    icon:
    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>

  }];


  return (
    <>
      {/* Top bar with green accent */}
      <div className="bg-gradient-to-r from-deep-navy via-dark-navy to-brand-green text-white py-3 hidden md:block border-b border-brand-green/20">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <p className="text-sm text-white/90 font-medium tracking-wide">
            {businessHours} &nbsp;|&nbsp; {businessLocation}
          </p>
          <div className="flex items-center gap-4">
            {socialLinks?.map((s) =>
            <a
              key={s?.label}
              href={s?.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s?.label}
              className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-brand-green hover:scale-110 transition-all duration-300 ease-out">
              
                {s?.icon}
              </a>
            )}
          </div>
        </div>
      </div>
      {/* Main nav with enhanced styling */}
      <header
        className={`sticky top-0 z-50 transition-all duration-500 ease-out ${
        isScrolled ?
        "bg-white shadow-lg border-b border-brand-green/10" :
        "bg-white/95 backdrop-blur-md"}`
        }>
        
        <nav className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between py-3">
          {/* Logo with hover effect */}
          <Link href="/home-page" className="flex-shrink-0 transition-transform duration-300 hover:scale-105">
            <AppLogo
              size={120}
              className="h-20 w-auto"
              priority={true}
            />
          </Link>

          {/* Desktop links with green theme */}
          <div className="hidden lg:flex items-center gap-6 ml-8 flex-nowrap overflow-hidden">
            {loading ? (
              <div className="text-sm text-body-text">Loading...</div>
            ) : (
              navLinks?.map((link) => {
                // Check if item has children for dropdown
                const hasChildren = link.children && link.children.length > 0;
                
                if (hasChildren) {
                  return (
                    <div key={link.id} className="relative group">
                      <button className="relative font-heading font-semibold text-[15px] text-dark-navy hover:text-brand-green transition-all duration-300 py-2">
                        {link.label}
                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-brand-green to-brand-green/60 group-hover:w-full transition-all duration-300 ease-out rounded-full"></span>
                      </button>
                      <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                        {link.children?.map((child) => (
                          <Link
                            key={child.id}
                            href={child.url}
                            className="block px-4 py-3 text-sm text-dark-navy hover:bg-brand-green/10 hover:text-brand-green transition-colors first:rounded-t-lg last:rounded-b-lg"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }
                
                return (
                  <Link 
                    key={link.id} 
                    href={link.url} 
                    className="relative font-heading font-semibold text-[15px] text-dark-navy hover:text-brand-green transition-all duration-300 group py-2">
                    {link.label}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-brand-green to-brand-green/60 group-hover:w-full transition-all duration-300 ease-out rounded-full"></span>
                  </Link>
                );
              })
            )}
          </div>

          {/* CTA buttons with improved styling */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            <a 
              href="/be-a-user" 
              className="relative px-6 py-3 text-sm font-heading font-bold text-brand-green border-2 border-brand-green rounded-full hover:bg-brand-green hover:text-white transition-all duration-300 ease-out hover:shadow-green-md hover:-translate-y-0.5">
              Be a User
            </a>
            <a 
              href="/be-a-driver" 
              className="px-6 py-3 text-sm font-heading font-bold text-white bg-gradient-to-r from-brand-green to-brand-green/90 rounded-full hover:shadow-green-md hover:-translate-y-0.5 transition-all duration-300 ease-out hover:from-brand-green/90 hover:to-brand-green">
              Be a Driver
            </a>
          </div>

          {/* Hamburger with green accent */}
          <button
            className="lg:hidden p-2.5 rounded-xl text-dark-navy hover:bg-brand-green/10 hover:text-brand-green transition-all duration-300"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu">
            
            {mobileOpen ?
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg> :

            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            }
          </button>
        </nav>

        {/* Mobile menu with green accents */}
        {mobileOpen &&
        <div className="lg:hidden bg-white/98 backdrop-blur-lg border-t border-brand-green/20 px-6 py-6 space-y-3 shadow-xl animate-fade-up">
            {loading ? (
              <div className="text-center py-4 text-body-text">Loading navigation...</div>
            ) : (
              navLinks?.map((link, index) => {
                const hasChildren = link.children && link.children.length > 0;
                
                if (hasChildren) {
                  return (
                    <div key={link.id}>
                      <div className="font-heading font-semibold text-[15px] text-dark-navy py-3 px-4">
                        {link.label}
                      </div>
                      {link.children?.map((child) => (
                        <Link
                          key={child.id}
                          href={child.url}
                          className="block font-heading font-medium text-[14px] text-body-text hover:text-brand-green hover:translate-x-2 transition-all duration-300 py-2 px-8 rounded-xl hover:bg-brand-green/5"
                          onClick={() => setMobileOpen(false)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={link.id}
                    href={link.url}
                    className="block font-heading font-semibold text-[15px] text-dark-navy hover:text-brand-green hover:translate-x-2 transition-all duration-300 py-3 px-4 rounded-xl hover:bg-brand-green/5"
                    onClick={() => setMobileOpen(false)}
                    style={{ animationDelay: `${index * 0.05}s` }}>
                    
                    {link.label}
                  </Link>
                );
              })
            )}
            <div className="flex flex-col gap-3 pt-4 border-t border-brand-green/20">
              <a 
                href="/be-a-user" 
                className="text-center py-3.5 px-6 font-heading font-bold text-brand-green border-2 border-brand-green rounded-full hover:bg-brand-green hover:text-white transition-all duration-300">
                Sign-Up as User
              </a>
              <a 
                href="/be-a-driver" 
                className="text-center py-3.5 px-6 font-heading font-bold text-white bg-gradient-to-r from-brand-green to-brand-green/90 rounded-full hover:shadow-green-md transition-all duration-300">
                Sign-Up as Driver
              </a>
            </div>
            <div className="flex items-center justify-center gap-4 pt-4">
              {socialLinks?.map((s) =>
            <a
              key={s?.label}
              href={s?.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s?.label}
              className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green hover:bg-brand-green hover:text-white hover:scale-110 transition-all duration-300">
              
                  {s?.icon}
                </a>
            )}
            </div>
          </div>
        }
      </header>
    </>
  );

}